// ABOUTME: Configures tenant and public translation loading for the frontend.
// ABOUTME: Isolates translation stores and pending requests while preserving the selected language.
import i18next, { i18n } from "i18next";
import { initReactI18next } from "react-i18next";
import Backend from "i18next-xhr-backend";
import {
    reportGenericInternalTranslation,
    TranslationPathSource
} from "./translationPathDiagnostic";
import {
    clearStoredPreferredLanguage,
    FALLBACK_LANGUAGE,
    getPreferredLanguage,
    getStoredPreferredLanguage,
    normalizeSupportedLanguage,
    getStoredI18nPath,
    persistPreferredLanguage,
    setStoredI18nPath
} from "./languagePreference";

const apiAddressRoot = import.meta.env.VITE_API_ADDRESS;
let activeInstance: i18n = i18next;
let activeContext = {
    initialization: Promise.resolve() as Promise<unknown>,
    languageQueue: Promise.resolve() as Promise<unknown>,
    pendingChanges: 0,
    committedLanguage: undefined as string | undefined
};
let activePath: string | undefined;
const instanceListeners = new Set<() => void>();

export const getTranslationInstance = () => activeInstance;

export const subscribeTranslationInstance = (listener: () => void) => {
    instanceListeners.add(listener);
    return () => {
        instanceListeners.delete(listener);
    };
};
// Active transition that owns translation loads right now. Route components write
// this during render (never in an effect): react-i18next can request namespaces
// while children render, before any parent effect runs, and the loadPath callback
// below reads it lazily at that moment to attribute generic-path diagnostics.
let translationPathSource: TranslationPathSource = "boot_default";

export const setTranslationPathSource = (source: TranslationPathSource) => {
    translationPathSource = source;
};

export const changeLanguageWithPersistence = async (
    language: string,
    instance = activeInstance
) => {
    // A menu from a retired context can finish handling an event after a switch.
    if (instance !== activeInstance) return;
    const context = activeContext;
    if (context.pendingChanges === 0) {
        context.committedLanguage =
            getStoredPreferredLanguage() || context.committedLanguage;
    }
    const preferredLanguage = persistPreferredLanguage(language) || language;
    context.pendingChanges += 1;
    const change = context.languageQueue.then(async () => {
        await context.initialization;
        if (instance !== activeInstance) return;
        try {
            await instance.changeLanguage(preferredLanguage);
            context.committedLanguage = normalizeSupportedLanguage(
                preferredLanguage
            );
        } catch (error) {
            if (instance !== activeInstance || context.pendingChanges > 1)
                throw error;
            if (context.committedLanguage) {
                persistPreferredLanguage(context.committedLanguage);
            } else {
                clearStoredPreferredLanguage();
            }
            throw error;
        }
    });
    context.languageQueue = change.catch(() => undefined);
    try {
        await change;
    } finally {
        context.pendingChanges -= 1;
    }
};

export const i18f = (
    url: string = `${apiAddressRoot}/general/translation/0/0`,
    source: TranslationPathSource = "boot_default"
) => {
    const preferredLanguage = getPreferredLanguage(
        activeInstance.language || activeInstance.languages?.[0]
    );

    setTranslationPathSource(source);
    setStoredI18nPath(url);
    persistPreferredLanguage(preferredLanguage);
    if (url === activePath) return activeInstance;

    // Each path owns its store and connector. Responses from a retired path
    // must never populate the current tenant or public context.
    const instance = i18next.createInstance();
    const context = {
        initialization: Promise.resolve() as Promise<unknown>,
        languageQueue: Promise.resolve() as Promise<unknown>,
        pendingChanges: 0,
        committedLanguage: preferredLanguage as string | undefined
    };
    activeContext = context;
    activeInstance = instance;
    activePath = url;
    instance.use(initReactI18next).use(Backend);
    instance.on("languageChanged", (language) => {
        if (instance === activeInstance && context.pendingChanges === 0) {
            context.committedLanguage = persistPreferredLanguage(language);
        }
    });
    const initialization = instance.init({
        // Immediate renders need the language hierarchy; backend resources still load asynchronously.
        initImmediate: false,
        ns: ["translation"],
        lng: preferredLanguage,
        fallbackLng: FALLBACK_LANGUAGE,
        keySeparator: false, // we do not use keys in form messages.welcome
        //  loadPath: '/locales/{{lng}}/{{ns}}.json',
        backend: {
            loadPath: (_languages: string[], namespaces: string[]) => {
                reportGenericInternalTranslation(
                    url,
                    source,
                    translationPathSource,
                    namespaces
                );
                return `${url}/{{lng}}/{{ns}}`;
            },
            crossDomain: true,
            allowMultiLoading: false
        },
        interpolation: {
            escapeValue: false // react already safes from xss
        },
        debug: false,
        load: "currentOnly"
    });

    context.initialization = initialization;
    const loadNamespaces = instance.loadNamespaces.bind(instance);
    instance.loadNamespaces = (namespaces, callback) => {
        // Register visible namespaces immediately so a language change during
        // initialization includes them in its resource request.
        const namespaceLoad = loadNamespaces(
            namespaces,
            (error, translation) => {
                void initialization.then(() => {
                    // react-i18next shares its mounted ref across instance changes;
                    // an old completion must not replace the current t.
                    if (instance === activeInstance)
                        callback?.(error, translation);
                });
            }
        );
        return initialization.then(() => namespaceLoad);
    };

    instanceListeners.forEach((listener) => listener());
    return instance;
};

export const bootI18n = () => {
    const currentPath = getStoredI18nPath();
    if (currentPath !== null && currentPath !== "") {
        i18f(currentPath, "boot_stored");
    } else {
        i18f();
    }
};

bootI18n();
